import type { Rng } from '../../core/rng';
import type { AABB, OBB, Segment, Vec2 } from '../../geo/types';
import type { SpatialHash } from '../../geo/spatialHash';
import { segmentIntersection } from '../../geo/polygon';
import type { Craft, GunSmokeEnd } from './harbourZones';
import { shipPlan, planBox } from './shipFootprint';
import { PAINTED_SAILS, PAINTED_TYPES } from './paintedTypes';
import {
  CRAFTS_BY_SIZE,
  SHIP_PAINTER_SCALE_H,
  broadsideHeading,
  clearOfPileRows,
  plansMeet,
  rejectionOf,
  shipDeal,
  shipOf,
  takePlan,
  takenPlans,
  typeOfCraft,
  type BowSide,
  type Clearances,
  type PalisadeSegment,
  type PlacedShip,
  type ShipDeal,
  type ShipPose,
} from './shipPlacement';

/** A vessel the painting shows in the harbour, at the ground the painter view draws it on. */
export interface BerthData {
  readonly id: string;
  readonly position: Vec2;
  readonly craft: Craft;
  readonly sailSet: boolean;
  readonly salute: readonly GunSmokeEnd[];
  /** The side of the painter frame its bow points to, where the painting shows it. */
  readonly bow?: BowSide;
}

/*
 * The painter overlaps neighbouring vessels on the canvas; in the painter view that would put one
 * hull through another, so a vessel whose painted spot another has taken, or which lies too near
 * the shore or a pile row for its hull, moves to the nearest clear spot on rings around it, a
 * step apart, never across a pile row.
 */
const NUDGE_STEP_M = 3;
const MAX_NUDGE_M = 120;

function ringsOut(step: number, reach: number): readonly Vec2[] {
  const rings = Array.from({ length: Math.floor(reach / step) }, (_, k) => {
    const radius = (k + 1) * step;
    const count = Math.ceil((2 * Math.PI * radius) / step);
    return Array.from({ length: count }, (__, i): Vec2 => {
      const angle = (2 * Math.PI * i) / count;
      return [radius * Math.cos(angle), radius * Math.sin(angle)];
    });
  });
  return [[0, 0], ...rings.flat()];
}

const NUDGES = ringsOut(NUDGE_STEP_M, MAX_NUDGE_M);

function crossesPileRow(rows: readonly PalisadeSegment[], from: Vec2, to: Vec2): boolean {
  const move = { a: from, b: to };
  return rows.some((p) => segmentIntersection(move, p.segment) !== null);
}

function paintedPlan(pose: ShipPose): OBB {
  return planBox(shipPlan(pose.type), pose.position, pose.heading, SHIP_PAINTER_SCALE_H);
}

function radiusOf(plan: OBB): number {
  return Math.hypot(plan.halfU, plan.halfV);
}

function around([x, y]: Vec2, reach: number): AABB {
  return { minX: x - reach, minY: y - reach, maxX: x + reach, maxY: y + reach };
}

function touches({ a, b }: Segment, box: AABB): boolean {
  return (
    Math.max(a[0], b[0]) >= box.minX &&
    Math.min(a[0], b[0]) <= box.maxX &&
    Math.max(a[1], b[1]) >= box.minY &&
    Math.min(a[1], b[1]) <= box.maxY
  );
}

/* The pile rows and the plans taken within reach of the spots a berth tries: no other can bar
   one of them. */
interface Reach {
  readonly rows: readonly PalisadeSegment[];
  readonly taken: readonly OBB[];
  readonly radii: Float64Array;
}

function withinReach(clearances: Clearances, taken: SpatialHash<OBB>, painted: ShipPose): Reach {
  const plan = paintedPlan(painted);
  const [x, y] = painted.position;
  const reach = MAX_NUDGE_M + Math.hypot(plan.center[0] - x, plan.center[1] - y) + radiusOf(plan);
  const near = taken.query(around(painted.position, reach));
  const moves = around(painted.position, MAX_NUDGE_M);
  return {
    rows: clearances.palisades.filter((row) => touches(row.segment, moves)),
    taken: near,
    radii: Float64Array.from(near, radiusOf),
  };
}

/* Rounding may shorten the distance between two circles that meet by a hair; this much slack
   keeps it from hiding a plan that meets the vessel's. */
const CIRCLE_SLACK_M = 1e-3;

/* The index of a plan taken within reach that meets the vessel's at this spot, or -1: the plans
   themselves are tested only where the circles about them meet. The spots a berth tries lie a step
   apart, so the plan that barred the spot before is tried first. */
function blockerWithinReach(
  reach: Reach,
  pose: ShipPose,
  circle: [Vec2, number],
  before: number
): number {
  const [[x, y], radius] = circle;
  const meets = (i: number): boolean => {
    const other = reach.taken[i]!;
    const gap = Math.hypot(other.center[0] - x, other.center[1] - y);
    return gap < reach.radii[i]! + radius + CIRCLE_SLACK_M && plansMeet(paintedPlan(pose), other);
  };
  if (before >= 0 && meets(before)) return before;
  return reach.taken.findIndex((_, i) => i !== before && meets(i));
}

/* The painted spot, or the nearest clear one on the rings about it. The spots barred by a pile
   row, a plan within reach or a hull too near a pile row are passed over; the first left open is
   held to every rejection. */
function nearestClear(
  clearances: Clearances,
  taken: SpatialHash<OBB>,
  painted: ShipPose
): ShipPose | null {
  const [x, y] = painted.position;
  const plan = paintedPlan(painted);
  const radius = radiusOf(plan);
  const reach = withinReach(clearances, taken, painted);
  let blocker = -1;
  /* Most spots a crowded berth tries lie across the pile row it moors beside; that test is the
     cheapest, so it goes first. The shore is the dearest, so it comes last. */
  for (const [dx, dy] of NUDGES) {
    const pose = { ...painted, position: [x + dx, y + dy] as Vec2 };
    if (crossesPileRow(reach.rows, painted.position, pose.position)) continue;
    const centre: Vec2 = [plan.center[0] + dx, plan.center[1] + dy];
    blocker = blockerWithinReach(reach, pose, [centre, radius], blocker);
    if (blocker >= 0 || !clearOfPileRows(clearances.palisades, pose)) continue;
    if (rejectionOf(clearances, taken, pose) === null) return pose;
  }
  return null;
}

function sizeRank(berth: BerthData): number {
  return CRAFTS_BY_SIZE.indexOf(berth.craft);
}

/**
 * Lays every painted vessel at its berth, broadside to the painter camera, in the craft, sail and
 * salute painted, a ship measured for its size as the type that size gives and any other as the
 * type dealt (its own deal unless the fleet's is given): the largest first, each at its painted
 * spot or the nearest clear one within reach, clear of the vessels placed before. The fleet keeps
 * the berths' order; a vessel with no clear spot within reach is left out and reported.
 */
export function placeBerthShips(
  berths: readonly BerthData[],
  clearances: Clearances,
  existing: readonly PlacedShip[],
  viewHeading: number,
  rng: Rng,
  warn: (message: string) => void,
  deal: ShipDeal = shipDeal(rng.fork('types'))
): readonly PlacedShip[] {
  const taken = takenPlans(existing);
  const bySize = berths
    .map((berth, index) => ({ berth, index }))
    .sort((a, b) => sizeRank(a.berth) - sizeRank(b.berth) || a.index - b.index);
  const placed = new Map<number, PlacedShip>();
  for (const { berth, index } of bySize) {
    const draw = rng.fork(berth.id);
    const type = PAINTED_TYPES[berth.id] ?? typeOfCraft(berth.craft, deal);
    const heading = broadsideHeading(viewHeading, draw, berth.bow);
    const pose = nearestClear(clearances, taken, { type, position: berth.position, heading });
    if (!pose) {
      warn(`Berth ${berth.id}: no clear water within ${MAX_NUDGE_M} m of its painted spot`);
      continue;
    }
    takePlan(taken, pose);
    const sailState = berth.sailSet ? (PAINTED_SAILS[berth.id] ?? 'set') : 'furled';
    placed.set(index, { ...shipOf(berth.id, pose), sailState, gunSmoke: berth.salute });
  }
  return berths.flatMap((_, index) => placed.get(index) ?? []);
}
